import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import compact from 'lodash/compact';
import { PaymentRequestItem } from '@stripe/stripe-js';

import { IPriceLocalized } from 'shared/types/Price';
import { PriceBreakdownType } from 'shared/enums/PriceBreakdown';
import { IPartialOrder } from 'checkout/types/Order';
import { STRIPE_ZERO_DECIMAL_CURRENCIES } from 'checkout/enums/StripeZeroDecimalCurrencies';
import { useCurrency } from 'shared/hooks/useCurrency';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { t } from 'localization/utils/t';
import { IAddOnOrders } from 'shared/types/AddOnOrders';

const TP = 'checkout.hooks.useCheckoutGlobalPricing';

export interface IGlobalPricingLineItem {
  disclaimer?: string;
  internationalCheckoutNote?: string;
  isNegative?: boolean;
  isRefund?: boolean;
  linkPriorityModal?: boolean;
  localizedPrice: IPriceLocalized;
  options?: { padded?: boolean };
  priceBreakdownTypes: string[];
  title: string;
  productTemplateId?: number;
}

interface ICheckoutGlobalPricingBreakdown {
  total: IGlobalPricingLineItem[];
  subtotal: IGlobalPricingLineItem[];
  offers: IGlobalPricingLineItem[];
  addOnOrders: IGlobalPricingLineItem[][];
}

export const useCheckoutGlobalPricing = () => {
  const { selectedCurrencyCode } = useCurrency();

  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isCheckoutGiftCardsEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS);
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );

  /**
   * Sneakers returns localized currencies in their lowest form however stripe
   * expects certain countries to display currencies not in their lowest form.
   * This format is currently only necessary for ApplePay
   * https://stripe.com/docs/currencies#zero-decimal
   */
  const formatStripeCurrencyAmount = (value) => {
    if (Number.isNaN(value)) return;

    return STRIPE_ZERO_DECIMAL_CURRENCIES.includes(selectedCurrencyCode)
      ? Number(value / 100)
      : value;
  };

  const isZero = (value) => {
    if (
      isNil(value) ||
      isEqual(value, false) ||
      isEqual(value, 0) ||
      isEqual(value, '0') ||
      isEqual(value, '$0.00')
    ) {
      return true;
    }

    return false;
  };

  // CHECKOUT TODO: subtotal amount is not a sum of all subtotal line items
  // but just the list price
  const getSubtotalLineItem = (subtotal) => {
    return {
      title: t(`${TP}.subtotal`, 'Subtotal'),
      localizedPrice: subtotal,
      priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
    };
  };

  const getAddOnGlobalPricingBreakdown = (addOnOrder: IAddOnOrders): IGlobalPricingLineItem[] => {
    const creditUsed = addOnOrder?.localizedCreditsUsedCents;
    const price = addOnOrder?.localizedPriceCents;
    const tax = addOnOrder?.localizedTaxCents;
    const total = addOnOrder?.localizedFinalPriceCents;
    const isFullyRefunded = addOnOrder?.fullyRefunded;
    const USDRefund = {
      currency: 'USD',
      amount: addOnOrder?.totalRefundedCents,
      amountUsdCents: addOnOrder?.totalRefundedCents,
    };

    return [
      {
        title: addOnOrder?.buyerTitle,
        localizedPrice: price,
        priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
      },
      !isZero(tax?.amount) && {
        title: t(`${TP}.tax`, 'Tax'),
        localizedPrice: tax,
        priceBreakdownTypes: [PriceBreakdownType.DEFAULT, PriceBreakdownType.SEPARATE_TAX],
      },
      !isZero(creditUsed?.amount) && {
        title: t(`${TP}.credit`, 'GOAT Credit'),
        isNegative: true,
        localizedPrice: creditUsed,
        priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
      },
      {
        title: t(`${TP}.total`, 'Total'),
        localizedPrice: total,
        priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
      },
      isFullyRefunded &&
        !isZero(USDRefund) && {
          title: t(`${TP}.refundIssued`, 'Refund Issued'),
          localizedPrice: USDRefund,
          isRefund: true,
          isNegative: true,
          priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
        },
    ];
  };

  const getCheckoutGlobalPricingBreakdown = (
    order: IPartialOrder,
  ): ICheckoutGlobalPricingBreakdown => {
    const credit = order?.localizedCreditCents;
    const creditUsed = order?.localizedCreditsUsedCents;
    const duty = order?.localizedDutyCents;
    const giftCard = order?.giftCards?.[0]; // For MPV, only allowing one gift card per order
    const instantShipMarkupFee = order?.localizedInstantShipMarkupFeeCents;
    const internationalCheckoutNote = order?.internationalCheckoutNote;
    const isGiftCard = order?.product?.productTemplate?.isGiftCardProduct;
    const isOffer = order?.wantId && isCheckoutOffersEnabled;
    const isRaffleProduct = order?.product?.productTemplate?.isRaffleProduct;
    const listPrice = order?.localizedListPriceCents;
    const applicableCosts = order?.localizedApplicableCostsCents;
    const priceBreakdownType = order?.priceBreakdownType;
    const shipping = order?.localizedShippingCents;
    const subtotal = order?.localizedPriceCents;
    const surcharge = order?.localizedSurchargeFeeCents;
    const tax = order?.localizedTaxCents;
    const total = order?.localizedFinalPriceCents;
    const vat = order?.localizedVatCents;
    const isFullyRefunded = order?.fullyRefunded;
    const USDRefund = {
      currency: 'USD',
      amount: order?.totalRefundedCents,
      amountUsdCents: order?.totalRefundedCents,
    };
    const USDTotal = {
      currency: 'USD',
      amount: order?.finalPriceCents,
      amountUsdCents: order?.finalPriceCents,
    };

    /**
     * NOTE: Currently only allowing one gift card per order
     */
    const hasAppliedGiftCard = !isEmpty(giftCard) && isCheckoutGiftCardsEnabled;

    const hasFees =
      !isZero(duty?.amount) ||
      !isZero(instantShipMarkupFee?.amount) ||
      !isZero(surcharge?.amount) ||
      !isZero(vat?.amount);

    return {
      total: [
        priceBreakdownType === PriceBreakdownType.SUBTOTAL_ONLY && getSubtotalLineItem(subtotal),
        !isZero(applicableCosts?.amount) && {
          title:
            priceBreakdownType === PriceBreakdownType.APPLICABLE_COSTS_SEPARATE_TAX
              ? t(`${TP}.applicableCosts`, 'Applicable Costs')
              : t(`${TP}.taxesAndApplicableCosts`, 'Taxes & Applicable Costs'),
          localizedPrice: applicableCosts,
          priceBreakdownTypes: [
            PriceBreakdownType.APPLICABLE_COSTS_SEPARATE_TAX,
            PriceBreakdownType.APPLICABLE_COSTS_SUBTOTAL_ONLY,
          ],
        },
        !isRaffleProduct &&
          !isGiftCard && {
            title: t(`${TP}.shipping`, 'Shipping'),
            localizedPrice: shipping,
            priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
          },
        !isZero(tax?.amount) && {
          title: t(`${TP}.tax`, 'Tax'),
          localizedPrice: tax,
          priceBreakdownTypes: [PriceBreakdownType.APPLICABLE_COSTS_SEPARATE_TAX],
        },
        !isZero(tax?.amount) && {
          title: t(`${TP}.estimatedTax`, 'Estimated Tax'),
          localizedPrice: tax,
          priceBreakdownTypes: [PriceBreakdownType.DEFAULT, PriceBreakdownType.SEPARATE_TAX],
        },
        // TODO: check and see which credit should be used
        !isZero(credit?.amount) && {
          title: t(`${TP}.credit`, 'GOAT Credit'),
          localizedPrice: credit,
          isNegative: true,
          priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
        },
        !isZero(creditUsed?.amount) && {
          title: t(`${TP}.credit`, 'GOAT Credit'),
          localizedPrice: creditUsed,
          isNegative: true,
          priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
        },
        hasAppliedGiftCard &&
          !isZero(giftCard?.localizedAmountToUseCents?.amount) && {
            title: t(`${TP}.giftCard`, 'GOAT Gift Card {codeLastFourChars}', {
              codeLastFourChars: giftCard?.codeLast4,
            }),
            localizedPrice: giftCard?.localizedAmountToUseCents,
            isNegative: true,
            priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
          },
        !isEmpty(giftCard) &&
          !isZero(giftCard?.localizedAmountUsedCents?.amount) && {
            title: t(`${TP}.goatGiftCard`, 'GOAT Gift Card'),
            localizedPrice: giftCard?.localizedAmountUsedCents,
            isNegative: true,
            priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
          },
        {
          title: t(`${TP}.total`, 'Total'),
          localizedPrice: isOffer ? USDTotal : total,
          priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
          ...(internationalCheckoutNote &&
            priceBreakdownType === PriceBreakdownType.SUBTOTAL_ONLY && {
              internationalCheckoutNote,
            }),
        },
        isFullyRefunded &&
          !isZero(order?.totalRefundedCents) && {
            title: t(`${TP}.refundIssued`, 'Refund Issued'),
            isNegative: true,
            isRefund: true,
            localizedPrice: USDRefund,
            priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
          },
      ],
      subtotal: [
        !isNil(listPrice?.amount) &&
          hasFees && {
            title: t(`${TP}.listPrice`, 'List Price'),
            localizedPrice: listPrice,
            priceBreakdownTypes: [
              PriceBreakdownType.DEFAULT,
              PriceBreakdownType.ITEMIZED,
              PriceBreakdownType.SEPARATE_TAX,
            ],
          },
        !isZero(instantShipMarkupFee?.amount) && {
          title: t(`${TP}.priorityProcessingFee`, 'Priority Processing'),
          localizedPrice: instantShipMarkupFee,
          priceBreakdownTypes: [
            PriceBreakdownType.DEFAULT,
            PriceBreakdownType.ITEMIZED,
            PriceBreakdownType.SEPARATE_TAX,
          ],
          linkPriorityModal: true,
        },
        !isZero(duty?.amount) && {
          title: t(`${TP}.duty`, 'Estimated Duties'),
          localizedPrice: duty,
          priceBreakdownTypes: [
            PriceBreakdownType.DEFAULT,
            PriceBreakdownType.ITEMIZED,
            PriceBreakdownType.SEPARATE_TAX,
          ],
        },
        !isZero(vat?.amount) && {
          title: t(`${TP}.vat`, 'Estimated VAT'),
          localizedPrice: vat,
          priceBreakdownTypes: [PriceBreakdownType.DEFAULT, PriceBreakdownType.ITEMIZED],
        },
        !isZero(surcharge?.amount) && {
          title: t(`${TP}.surcharges`, 'Surcharges'),
          localizedPrice: surcharge,
          priceBreakdownTypes: [PriceBreakdownType.ITEMIZED, PriceBreakdownType.SEPARATE_TAX],
        },
      ],
      offers: [
        {
          title: t(`${TP}.approximateTotal`, 'Approximate Total'),
          localizedPrice: total,
          priceBreakdownTypes: [PriceBreakdownType.ALWAYS_SHOW],
        },
      ],
      addOnOrders: order?.addOnOrders?.map((addOnOrder) =>
        getAddOnGlobalPricingBreakdown(addOnOrder),
      ),
    };
  };

  /**
   * We cannot rely on the state order since for apple pay we do not send any
   * information and only create the order object that we send to sneakers after
   * the user has submitted payment. Therefore must always pass in the latest
   * order via params
   */
  const getStripeCheckoutGlobalPricingBreakdown = (order: IPartialOrder): PaymentRequestItem[] => {
    const credit = order?.localizedCreditCents;
    const duty = order?.localizedDutyCents;
    const giftCard = order?.giftCards?.[0];
    /**
     * NOTE: fallback for guest express checkout since no price breakdown type is available
     * until BE team fixes will keep default as the main fallback.
     */
    const priceBreakdownType = order?.priceBreakdownType || PriceBreakdownType.DEFAULT;
    const shipping = order?.localizedShippingCents;
    const subtotal = order?.localizedPriceCents;
    const surcharge = order?.localizedSurchargeFeeCents;
    const tax = order?.localizedTaxCents;
    const vat = order?.localizedVatCents;
    const isPurchasingGiftCard = !!order?.product?.giftCardId && isCheckoutPurchaseGiftCardsEnabled;

    /**
     * NOTE: Currently only allowing one gift card per order
     */
    const hasAppliedGiftCard = !isEmpty(giftCard) && isCheckoutGiftCardsEnabled;

    return compact([
      {
        label: t(`${TP}.subtotal`, 'Subtotal'),
        amount: formatStripeCurrencyAmount(Number(subtotal?.amount) || 0),
      },
      !isPurchasingGiftCard && {
        label: t(`${TP}.shipping`, 'Shipping'),
        amount: formatStripeCurrencyAmount(Number(shipping?.amount) || 0),
      },
      !isZero(tax?.amount) &&
        (priceBreakdownType === PriceBreakdownType.DEFAULT ||
          priceBreakdownType === PriceBreakdownType.SEPARATE_TAX) && {
          label: t(`${TP}.estimatedTax`, 'Estimated Tax'),
          amount: formatStripeCurrencyAmount(Number(tax?.amount) || 0),
        },
      !isZero(duty?.amount) &&
        (priceBreakdownType === PriceBreakdownType.DEFAULT ||
          priceBreakdownType === PriceBreakdownType.ITEMIZED ||
          priceBreakdownType === PriceBreakdownType.SEPARATE_TAX) && {
          label: t(`${TP}.duty`, 'Estimated Duties'),
          amount: formatStripeCurrencyAmount(Number(duty?.amount)),
        },
      !isZero(vat?.amount) &&
        (priceBreakdownType === PriceBreakdownType.DEFAULT ||
          priceBreakdownType === PriceBreakdownType.ITEMIZED) && {
          label: t(`${TP}.vat`, 'Estimated VAT'),
          amount: formatStripeCurrencyAmount(Number(vat?.amount)),
        },
      !isZero(surcharge?.amount) &&
        (priceBreakdownType === PriceBreakdownType.ITEMIZED ||
          priceBreakdownType === PriceBreakdownType.SEPARATE_TAX) && {
          label: t(`${TP}.surcharges`, 'Surcharges'),
          amount: formatStripeCurrencyAmount(Number(surcharge?.amount)),
        },
      !isZero(credit?.amount) &&
        !isPurchasingGiftCard && {
          label: t(`${TP}.credit`, 'GOAT Credit'),
          amount: formatStripeCurrencyAmount(Number(credit?.amount) * -1),
        },
      hasAppliedGiftCard &&
        !isZero(giftCard?.localizedAmountToUseCents?.amount) && {
          label: t(`${TP}.giftCard`, 'GOAT Gift Card {codeLastFourChars}', {
            codeLastFourChars: giftCard?.codeLast4,
          }),
          amount: formatStripeCurrencyAmount(
            Number(giftCard?.localizedAmountToUseCents?.amount) * -1,
          ),
        },
    ]);
  };

  return {
    formatStripeCurrencyAmount,
    getCheckoutGlobalPricingBreakdown,
    getStripeCheckoutGlobalPricingBreakdown,
    getSubtotalLineItem,
  };
};
